import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabaseClient';

const MemberHistoryModal = ({ isOpen, onClose, member }) => {
  const [matchHistory, setMatchHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    totalSessions: 0,
    paidSessions: 0,
    totalPayments: 0,
    membershipDays: 0,
    lastActivity: null
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !member) return;

    const fetchMatchHistory = async () => {
      setIsLoading(true);
      try {
        const { data: sessions, error } = await supabase
          .from('session_invites')
          .select('id, title, venue, date, start_time, price, participants, created_at')
          .order('date', { ascending: false });

        if (error) {
          console.error('Failed to fetch sessions:', error);
          return;
        }

        const memberPhone = member?.phone?.trim();
        const memberName = member?.name?.trim();

        const participated = (sessions || [])
          .map((session) => {
            const participants = Array.isArray(session?.participants) ? session.participants : [];
            const found = participants.find(
              (p) => p?.phone?.trim() === memberPhone && p?.name?.trim() === memberName
            );
            if (!found) return null;
            return {
              id: session?.id,
              title: session?.title || '세션',
              venue: session?.venue || '-',
              date: session?.date,
              startTime: session?.start_time,
              price: session?.price ?? 0,
              paymentStatus: found?.payment || 'pending',
              registeredAt: found?.registeredAt || session?.created_at,
              status: found?.status || 'confirmed'
            };
          })
          .filter(Boolean);

        setMatchHistory(participated);

        const paidCount = participated.filter((p) => p.paymentStatus === 'paid').length;
        const totalPaid = participated
          .filter((p) => p.paymentStatus === 'paid')
          .reduce((sum, p) => sum + (p.price || 0), 0);

        const registeredDate = member?.registeredAt ? new Date(member.registeredAt) : null;
        const daysSince = registeredDate
          ? Math.floor((Date.now() - registeredDate.getTime()) / (1000 * 60 * 60 * 24))
          : 0;

        setStats({
          totalSessions: participated.length,
          paidSessions: paidCount,
          totalPayments: totalPaid,
          membershipDays: daysSince,
          lastActivity: participated.length > 0 ? participated[0].date : null
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatchHistory();
  }, [isOpen, member]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('ko-KR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !member) return null;

  const getPaymentLabel = (status) => {
    switch (status) {
      case 'paid': return { label: '결제완료', color: 'text-success', bg: 'bg-success/10' };
      case 'pending': return { label: '미결제', color: 'text-warning', bg: 'bg-warning/10' };
      default: return { label: '-', color: 'text-muted-foreground', bg: 'bg-muted' };
    }
  };

  const formatSessionDate = (dateStr, timeStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const formatted = d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
    return timeStr ? `${formatted} ${timeStr}` : formatted;
  };

  return (
    <div
      className="fixed inset-0 z-300 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-background z-250" aria-hidden="true" />
      <div className="relative z-300 w-full max-w-3xl bg-card rounded-xl shadow-2xl border border-border my-8">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-heading font-bold text-primary data-text">
                  {member?.skillLevel}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-heading font-semibold text-foreground mb-1">
                  {member?.name}
                </h2>
                <p className="text-sm caption text-muted-foreground mb-2">{member?.phone}</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-md bg-primary/10 text-primary text-sm font-medium">
                    {member?.skillDescription}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press"
              aria-label="닫기"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Calendar" size={16} className="text-muted-foreground" />
                <span className="text-sm caption text-muted-foreground">총 세션</span>
              </div>
              <p className="text-2xl font-heading font-bold text-foreground data-text">
                {stats?.totalSessions}
              </p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CheckCircle" size={16} className="text-muted-foreground" />
                <span className="text-sm caption text-muted-foreground">결제 완료</span>
              </div>
              <p className="text-2xl font-heading font-bold text-foreground data-text">
                {stats?.paidSessions}
              </p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="DollarSign" size={16} className="text-muted-foreground" />
                <span className="text-sm caption text-muted-foreground">총 결제액</span>
              </div>
              <p className="text-2xl font-heading font-bold text-foreground data-text">
                ₩{stats?.totalPayments?.toLocaleString()}
              </p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Activity" size={16} className="text-muted-foreground" />
                <span className="text-sm caption text-muted-foreground">최근 활동</span>
              </div>
              <p className="text-sm font-medium text-foreground">
                {stats?.lastActivity 
                  ? new Date(stats.lastActivity)?.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
                  : '-'}
              </p>
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-4">매치 신청 기록</h3>
            {isLoading ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">불러오는 중...</p>
              </div>
            ) : matchHistory?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Calendar" size={40} className="mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">매치 신청 기록이 없습니다</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-custom">
                {matchHistory?.map((match) => {
                  const paymentConfig = getPaymentLabel(match?.paymentStatus);
                  return (
                    <div 
                      key={match?.id}
                      className="bg-muted rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-heading font-medium text-foreground">
                          {match?.title}
                        </h4>
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${paymentConfig.bg} ${paymentConfig.color}`}>
                          {paymentConfig.label}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="MapPin" size={14} />
                          <span>{match?.venue || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="Calendar" size={14} />
                          <span>{formatSessionDate(match?.date, match?.startTime)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="DollarSign" size={14} />
                          <span>₩{(match?.price || 0)?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-6 border-t border-border mt-6">
            <Button
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              닫기
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberHistoryModal;